import React from 'react';
import { Divider, Grid, ListItem, Tooltip } from '@mui/material';
import CreditSafeComp from 'components/CreditSafeComp';
import { RowNameText } from './RowNameText';
import { CreditApplication, VendorOpportunity, VendorProfile } from '@trnsact/trnsact-shared-types';
import { ApolloQueryResult } from '@apollo/client';

interface ProviderConfig {
  label: string;
  pullLabel: string;
  tooltip: string;
}

const PROVIDER_CONFIGS: Record<string, ProviderConfig> = {
  PAYNET: { label: 'Paynet Report', pullLabel: 'Pull Paynet', tooltip: 'Pulling via PayNet' },
  CREDITSAFE_FULL_REPORT: { label: 'CreditSafe Report', pullLabel: 'Pull CreditSafe', tooltip: '' },
  DUN_AND_BRADSTREET: { label: 'Dun & Bradstreet Report', pullLabel: 'Pull Dun & Bradstreet', tooltip: '' },
  EQUIFAX_COMMERCIAL: {
    label: 'Paynet Report',
    pullLabel: 'Pull Paynet',
    tooltip: 'Pulling via Equifax Commercial',
  },
};

/**
 * Returns true when the given provider should be shown for the credit app.
 *
 * Country rules (DR-10037):
 *   EQUIFAX_COMMERCIAL  → USA applicants only
 *   PAYNET              → Canada applicants only
 *   DUN_AND_BRADSTREET  → any country, individual and commercial
 *   CREDITSAFE_FULL_REPORT → any country, commercial apps only
 */
const isProviderVisible = (provider: string, isIndividualCreditApp: boolean, appCountry: string): boolean => {
  switch (provider) {
    case 'EQUIFAX_COMMERCIAL':
      return appCountry === 'US';
    case 'PAYNET':
      // return appCountry === 'CA';
      return true;
    case 'CREDITSAFE_FULL_REPORT':
      return !isIndividualCreditApp;
    case 'DUN_AND_BRADSTREET':
      return true;
    default:
      return false;
  }
};

interface Props {
  vp: VendorProfile;
  vo: VendorOpportunity;
  creditApplication: CreditApplication;
  userDocs: any;
  isIndividualCreditApp: boolean;
  isCreditReportsHiddenForUserRole: boolean;
  userDocsRefetch: () => Promise<ApolloQueryResult<void>>;
  refetchCreditApp: () => Promise<ApolloQueryResult<void>>;
  setDoneRefetchingDocuments: any;
  disableGutters?: boolean;
}

export const PullCreditReportRow = ({
  vp,
  vo,
  creditApplication,
  userDocs,
  isIndividualCreditApp,
  isCreditReportsHiddenForUserRole,
  userDocsRefetch,
  refetchCreditApp,
  setDoneRefetchingDocuments,
  disableGutters = false,
}: Props) => {
  if (isCreditReportsHiddenForUserRole) return null;

  const enabledProviders = (vp?.settingBusinessCreditReportPullInDealerPortal as unknown as string[]) ?? [];
  // Default to 'US' when country is not set on the credit application
  const appCountry = creditApplication?.country ?? 'US';

  const visibleProviders = enabledProviders.filter(provider =>
    isProviderVisible(provider, isIndividualCreditApp, appCountry)
  );

  if (visibleProviders.length === 0) return null;

  return (
    <>
      {visibleProviders.map(provider => {
        const { label, pullLabel, tooltip } = PROVIDER_CONFIGS[provider] ?? {
          label: 'Credit Report',
          pullLabel: 'Pull Credit Report',
          tooltip: '',
        };
        return (
          <React.Fragment key={provider}>
            <ListItem disableGutters={disableGutters}>
              <Grid container>
                <Grid item xs={6}>
                  <RowNameText text={label} />
                </Grid>
                <Grid item xs={6}>
                  <Tooltip title={tooltip} disableHoverListener={!tooltip}>
                    <span>
                      <CreditSafeComp
                        vo={vo}
                        creditApp={creditApplication}
                        userDocs={userDocs}
                        openCreditPullErrorSnackBar={() => {
                          alert('Credit Pull Error');
                        }}
                        setCreditPullErrorSnackBarText={() => {
                          console.log('Credit Pull Error');
                        }}
                        userDocsRefetch={userDocsRefetch}
                        refetchCreditApp={refetchCreditApp}
                        CbrSoftPullTooltip={<div>Contact Support</div>}
                        setDoneRefetchingDocuments={setDoneRefetchingDocuments}
                        vpBusinessCreditReportPullInDealerPortal={provider}
                        commercialCreditReportPullButtonLabel={pullLabel}
                        isIndividualCreditApp={isIndividualCreditApp}
                      />
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </ListItem>
            <Divider />
          </React.Fragment>
        );
      })}
    </>
  );
};
